/**
 * Login context for authentication state management
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import matrixAuthService from '../services/matrixAuthService';
import indexedDBService from '../services/indexedDB';
import logger from '../utils/logger';
import { generateInitialsAvatar } from '../utils/avatar';
// import { ModuleRunner } from './ModuleRunner';

const LoginContext = createContext();

export const useLogin = () => {
  const context = useContext(LoginContext);
  if (!context) {
    throw new Error('useLogin must be used within LoginProvider');
  }
  return context;
};

export const LoginProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing login state on app start
  useEffect(() => {
    const checkLoginState = async () => {
      try {
        logger.debug('LoginContext: Checking for existing login state...');
        
        // Initialize IndexedDB
        try {
          await indexedDBService.init();
          logger.info('IndexedDB initialized successfully');
        } catch (error) {
          logger.error('Failed to initialize IndexedDB:', error);
          // App can still work without cache
        }
        
        // Check for Matrix credentials in localStorage
        const savedMatrixCreds = localStorage.getItem('matrixCreds');
        if (savedMatrixCreds) {
          try {
            const matrixCreds = JSON.parse(savedMatrixCreds);
            logger.debug('LoginContext: Found Matrix credentials in localStorage for user:', matrixCreds.userId);
            
            // Create user object from Matrix credentials
            const userName = matrixCreds.userId.split(':')[0].substring(1); // Extract username from @user:server
            const user = {
              id: matrixCreds.userId,
              name: userName || matrixCreds.userId,
              username: matrixCreds.userId,
              access_token: matrixCreds.accessToken,
              homeserver: matrixCreds.homeserverUrl,
              avatar: generateInitialsAvatar(userName || matrixCreds.userId), // Generate avatar from initials
            };
            
            setCurrentUser(user);
            setIsAuthenticated(true);
            logger.debug('LoginContext: User restored from Matrix credentials');
          } catch (error) {
            logger.error('LoginContext: Error parsing Matrix credentials:', error);
            localStorage.removeItem('matrixCreds');
          }
        } else {
          logger.debug('LoginContext: No valid login state found');
        }
      } catch (error) {
        logger.error('LoginContext: Error checking login state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkLoginState();
  }, []);

  // Matrix login helper using auth service
  const performMatrixLogin = async (credentials) => {
    const result = await matrixAuthService.login(credentials);
    
    if (result.success) {
      return result.credentials;
    } else {
      throw new Error(result.error?.message || 'Matrix login failed');
    }
  };

  const login = useCallback(async (userData) => {
    try {
      setIsLoading(true);

      // Perform Matrix login
      const creds = await performMatrixLogin({
        username: userData.username,
        password: userData.password,
      });
      
      // Create user object from Matrix credentials
      const userName = userData.username.startsWith('@') ? userData.username.split(':')[0].substring(1) : userData.username;
      const matrixUser = {
        id: creds.userId,
        name: userName,
        username: userData.username,
        matrixUserId: creds.userId,
        homeserver: creds.homeserverUrl,
        access_token: creds.accessToken, // Add access token for Matrix integration
        avatar: generateInitialsAvatar(userName), // Generate avatar from initials
      };
      
      // Update state
      setCurrentUser(matrixUser);
      setIsAuthenticated(true);

      return { success: true, creds, user: matrixUser };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Note: authenticateUser and getOrCreateUser were removed as they're not used
  // The app now uses Matrix-only authentication via the login() function

  const logout = useCallback(async (reason) => {
    try {
      setIsLoading(true);
      logger.info('LOGOUT: Starting logout from LoginContext');

      // Logout from Matrix and clear all Matrix-related data
      logger.debug('LOGOUT: Calling matrixAuthService.logout()');
      await matrixAuthService.logout();

      // Clear app's IndexedDB database using the service
      try {
        logger.debug('LOGOUT: Deleting app IndexedDB database');
        await indexedDBService.deleteDatabase();
        logger.debug('LOGOUT: App database deleted successfully');
      } catch (error) {
        logger.error('LOGOUT: Failed to clear app database:', error);
      }

      // Double-check that all localStorage is cleared
      // (matrixAuthService.logout() should have done this, but ensure it's complete)
      try {
        logger.debug('LOGOUT: Double-checking localStorage cleanup');
        const allLocalStorageKeys = Object.keys(localStorage);
        allLocalStorageKeys.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            logger.debug(`LOGOUT: Could not remove localStorage key ${key}:`, e);
          }
        });
      } catch (error) {
        logger.error('LOGOUT: Failed to clear all localStorage:', error);
      }

      // Clear all sessionStorage as well
      try {
        logger.debug('LOGOUT: Final sessionStorage clear from LoginContext');
        sessionStorage.clear();
        if (reason === 'inactivity') {
          // set afterward to show modal
          sessionStorage.setItem('logout_reason', 'inactivity');
        }
      } catch (error) {
        logger.error('LOGOUT: Failed to clear sessionStorage:', error);
      }

      // Reset state
      setCurrentUser(null);
      setIsAuthenticated(false);

      logger.info('LOGOUT: Completed - all data cleared from LoginContext');

      return { success: true };
    } catch (error) {
      logger.error('Logout error:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = {
    isAuthenticated,
    currentUser,
    setCurrentUser, // Export setCurrentUser for AvatarSync component
    isLoading,
    login,
    logout,
    setIsAuthenticated,
  };

  return (
    <LoginContext.Provider value={value}>
      {children}
    </LoginContext.Provider>
  );
};

LoginProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LoginContext;
